import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Country } from '../models/location/country';
import { StateProvince } from '../models/location/state-province';
import { Station } from '../models/location/station';
import { Region } from '../models/location/region';
import { catchError, combineLatest, map, Observable, BehaviorSubject } from 'rxjs';
import { DataQueryService } from './data-query/data-query.service';
import { DataQueryServiceConfiguration } from './data-query/data-query-service-configuration';

@Injectable({
  providedIn: 'root'
})
export class LocationDataService extends DataQueryService {
    prefetchdata(dashboardType: string): Observable<any> {
        var filters = { filtertype: dashboardType };
        return combineLatest([
            this.prefetchGet(LocationDataServiceConfiguration.CountriesByDashboardType(dashboardType,'')),
            this.prefetchPost(LocationDataServiceConfiguration.DashboardStatesProvinces, filters),
            this.prefetchPost(LocationDataServiceConfiguration.DashboardStations, filters),
            this.prefetchPost(LocationDataServiceConfiguration.RegionsByDashboardType, filters)
        ]);
    }
    private dataUpdatedSource = new BehaviorSubject<boolean>(false);
    dataUpdated$ = this.dataUpdatedSource.asObservable();

    notifyDataUpdated(updated: boolean) {
        this.dataUpdatedSource.next(updated);
    }

    getCountries(dashboardType: string = '', regions: string[] = []) {
        let regionParam: string = (regions ?? []).join(",");
        var filters = { filtertype: dashboardType, regions: regionParam };
        return this.post(LocationDataServiceConfiguration.DashboardCountries, filters);
    }

    getStatesProvinces(dashboardType: string = '', subRegions: string[] = [], stateCodes: string[] = []) {
        let subRegionParam: string = (subRegions ?? []).join(",");
        let stateCodeParam: string = (stateCodes ?? []).join(",");
        var filters = { filtertype: dashboardType, subRegions: subRegionParam, stationCode: stateCodeParam  };
        return this.post(LocationDataServiceConfiguration.DashboardStatesProvinces, filters);
    }


    getFilterStations(groupBySplc: boolean, stationType: string = '', originDestination = '', dashboardType: string = '', stateProvinces: string[] = []) {
        let stateProvincesParam: string = (stateProvinces ?? []).join(",");
        var filters = { filtertype: dashboardType, stateProvinces: stateProvincesParam, groupBySplc: groupBySplc, stationType: stationType, originDestination: originDestination };
        return this.post(LocationDataServiceConfiguration.DashboardStations, filters);
    }

    getSubRegions(originDestination: string, cycleTabType: string) {
        return this.get<string[]>(LocationDataServiceConfiguration.getSubRegion(originDestination, cycleTabType));
    }

    getDashboardFilterSubRegions(dashboardType: string, regions: string[] = [], stateProvinces: string[]=[]) {
        let regionParam: string = (regions ?? []).join(",");
        let stateProvincesParam: string = (stateProvinces ?? []).join(",");
        var filters = { filtertype: dashboardType, regions: regionParam, stateProvinces: stateProvincesParam };
        return this.post(LocationDataServiceConfiguration.getDashboardFilterSubRegion, filters);
    }

    getStationsTest(groupBySplc: boolean, stationType: string = '', originDestination = '', dashboardType: string = '') {
        if (!!groupBySplc) {
            if (dashboardType) {
                return this.get<Station[]>(LocationDataServiceConfiguration.DashboardStationsWithSplc123(dashboardType));
            }
            else {
                return this.get<Station[]>(LocationDataServiceConfiguration.AllStationsWithSplc);
            }
        }
        else {
            return this.query({
                queryKey: ['stations-railcar', stationType, originDestination] as const,
                queryFn: () => this.http.get<Station[]>(LocationDataServiceConfiguration.DashboardStations.url, {
                    params: new HttpParams({
                        fromObject: {
                            groupBySplc: false,
                            stationType,
                            filterType: dashboardType
                        }
                    })
                }).pipe(map((results) => {
                    if (originDestination == 'origin') {
                        return results.filter((station) => station.type == 'origin');
                    }
                    else if (originDestination == 'destination') {
                        return results.filter((station) => station.type == 'destination');
                    }
                    else {
                        return results;
                    }
                }), catchError((error) => this.handleError(error))),
                throwOnError: false,
                staleTime: 0,
                retry: false
            });
        }
    }

    getRegions(dashboardType: string = '', countries: string[] = [], subRegions: string[] = [], ) {
        let countryParam: string = (countries ?? []).map(country => country).join(",");
        let subRegionParam: string = (subRegions ?? []).join(",");
        var filters = { filtertype: dashboardType, countries: countryParam, subRegions: subRegionParam };
        return this.post(LocationDataServiceConfiguration.RegionsByDashboardType, filters);
    }
}

abstract class LocationDataServiceConfiguration {

    static readonly AllStationsWithSplc = new DataQueryServiceConfiguration<string[]>(
        'stations-splc',
        environment.apiUrl + 'Location/Stations?groupBySplc=true',
        0
    );

    static DashboardStatesProvinces = new DataQueryServiceConfiguration<StateProvince[]>(
        'states-provinces',
        environment.apiUrl + 'Location/States',
        0
    );

    static DashboardStations = new DataQueryServiceConfiguration<Station[]>(
        `stations-railcar`,
        environment.apiUrl + 'Location/Stations',
        0
    );

    static DashboardCountries = new DataQueryServiceConfiguration<Country[]>(
        `stations-railcar`,
        environment.apiUrl + 'Location/Countries',
        0
    );

    static DashboardStationsWithSplc123(dashboardType: string): DataQueryServiceConfiguration<Station[]> {
        return new DataQueryServiceConfiguration<Station[]>(
            `stations-splc-${dashboardType}`,
            environment.apiUrl + 'Location/Stations?groupBySplc=true&filterType=' + encodeURIComponent(dashboardType),
            0
        );
    }

    static CountriesByDashboardType(dashboardType: string, regions: string): DataQueryServiceConfiguration<Country[]> {
        return new DataQueryServiceConfiguration<Country[]>(
            `countries-${dashboardType}`,
            environment.apiUrl + 'Location/Countries?filterType=' + encodeURIComponent(dashboardType) + '&regions=' + regions,
            0
        );
    }

    static RegionsByDashboardType = new DataQueryServiceConfiguration<Region[]>(
        `region`,
        environment.apiUrl + 'Location/Regions',
        0
    );

    static getSubRegion(filterType: string, cycleTabType: string): DataQueryServiceConfiguration<string[]> {
        return new DataQueryServiceConfiguration<string[]>(
            `sub-region-${filterType}-${cycleTabType}`,
            environment.apiUrl + 'Location/SubRegions?filterType=' + filterType + '&cycleTabType=' + cycleTabType,
            0
        );
    }
    static getDashboardFilterSubRegion = new DataQueryServiceConfiguration<string[]>(
        `dashboard-filter-sub-region`,
        environment.apiUrl + 'Location/FilterSubRegions',
        0
    );


}
